import { HandOverlayButton } from "@kalyzee/escaet-ui";
import { MeetingSessionHighlightingState } from "@kalyzee/kast-web-sdk/websocket";
import React, { useEffect, useRef } from "react";
import { sdkClient } from "../../helpers/sdk";
import { toastError, toastInfo, toastSuccess } from "../../helpers/toast";
import { useFocusedMeetingAPI, useMeetingSelector } from "../../hooks/meeting";
import { SessionMode } from "../../store/session/slices";
import { PageMasterMode, usePageContext } from "../navigation/PageContext";
import OverlayInformationMessage from "../utils/OverlayInformationMessage";

const OVERLAY_MESSAGES = {
  noPermitted: "Vous ne pouvez actuellement pas monter au tableau.",
  [MeetingSessionHighlightingState.INACTIVE]: "Envoyer une demande de montée au tableau",
  [MeetingSessionHighlightingState.ONGOING]: "Arrêter la montée au tableau",
  [MeetingSessionHighlightingState.PENDING_REQUEST]: "Annuler la demande de montée au tableau",
} as const;

export interface HighlightingProps {}

export const Highlighting = React.memo(({}: HighlightingProps) => {
  const pageContext = usePageContext<SessionMode.MASTER>();

  const highlightingRef = useRef(false);
  const highlightingRequestStateRef = useRef<"idle" | "running" | "canceled">("idle");
  const buttonContainerRef = useRef<HTMLDivElement>(null);

  const meetingCtx = useFocusedMeetingAPI();
  const [meetingSession] = useMeetingSelector(meetingCtx, state => state.session);

  useEffect(() => {
    if (meetingSession?.highlighting === MeetingSessionHighlightingState.ONGOING) {
      if (highlightingRequestStateRef.current === "running") {
        toastSuccess("Demande de montée au tableau acceptée");
      }
      pageContext?.goTo(PageMasterMode.MEETING_HIGHLIGHT, { id: pageContext?.params?.id });
      highlightingRef.current = true;
      highlightingRequestStateRef.current = "idle";
    } else if (meetingSession?.highlighting === MeetingSessionHighlightingState.INACTIVE) {
      if (highlightingRef.current) {
        toastInfo("Montée au tableau terminée");
        highlightingRef.current = false;
      } else if (highlightingRequestStateRef.current === "running") {
        toastInfo("Demande de montée au tableau refusée");
        highlightingRequestStateRef.current = "idle";
      }
    }
  }, [meetingSession?.highlighting]);

  if (!meetingSession) return null;

  const { id: sessionId, highlighting } = meetingSession;

  const onRequestHighlight = async () => {
    const wsClient = sdkClient.getWebsocketClient();

    if (highlighting === MeetingSessionHighlightingState.ONGOING) {
      const response = await wsClient.emitter.meeting.stopHighlighting({
        sessionId,
      });
      if (!response.response || response.error) {
        toastError("Une erreur est apparue...");
        return;
      }
      return;
    }

    if (highlightingRequestStateRef.current === "running" || highlighting === MeetingSessionHighlightingState.PENDING_REQUEST) {
      highlightingRequestStateRef.current = "canceled";
      const response = await wsClient.emitter.meeting.stopHighlighting({
        sessionId,
      });

      if (!response.response || response.error) {
        highlightingRequestStateRef.current = "running";
        toastError("Une erreur est apparue...");
        return;
      }
      highlightingRequestStateRef.current = "idle";
      toastInfo("Demande annulée");
      return;
    }

    const response = await wsClient.emitter.meeting.requestForHighlighting({
      sessionId,
    });
    if (response.response && !response.error) {
      toastSuccess("Demande envoyée");
      highlightingRequestStateRef.current = "running";
    } else {
      toastError("Erreur lors de l'envoi de la demande");
    }
  };

  const permissions = meetingSession.permissions;
  if (permissions?.highlighting === false) {
    return (
      <>
        <div ref={buttonContainerRef}>
          <HandOverlayButton
            key="student-dashboard-overlay-hand-btn"
            // disabled={meetingSession?.highlighting !== MeetingSessionHighlightingState.INACTIVE}
            state={
              meetingSession?.highlighting === MeetingSessionHighlightingState.ONGOING
                ? "in progress"
                : meetingSession?.highlighting === MeetingSessionHighlightingState.PENDING_REQUEST
                ? "pending"
                : "idle"
            }
            onClick={onRequestHighlight}
            disabled
          />
          <OverlayInformationMessage targetRef={buttonContainerRef} message={OVERLAY_MESSAGES.noPermitted} />
        </div>
      </>
    );
  }

  return (
    <>
      <div ref={buttonContainerRef}>
        <HandOverlayButton
          key="student-dashboard-overlay-hand-btn"
          // disabled={meetingSession?.highlighting !== MeetingSessionHighlightingState.INACTIVE}
          state={
            meetingSession?.highlighting === MeetingSessionHighlightingState.ONGOING
              ? "in progress"
              : meetingSession?.highlighting === MeetingSessionHighlightingState.PENDING_REQUEST
              ? "pending"
              : "idle"
          }
          onClick={onRequestHighlight}
        />
        {meetingSession?.highlighting && OVERLAY_MESSAGES[meetingSession.highlighting] ? (
          <OverlayInformationMessage targetRef={buttonContainerRef} message={OVERLAY_MESSAGES[meetingSession.highlighting]} />
        ) : null}
      </div>
    </>
  );
});
